import React, {useState, useEffect } from 'react';
import { useNavigate} from "react-router-dom";
import axios from 'axios';
import './TeamPage.css';
import './Auth.css';

const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const TeamPage = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const token = localStorage.getItem('tankly_token');
    const navigate = useNavigate();
    let currentUserEmail = '';
    let isAdmin = false;

    if (token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            isAdmin = payload.role === 'ROLE_COMPANY_ADMIN';
            currentUserEmail = payload.sub;
        }
        catch (e) {
            console.error("Token error", e);
        }
    }

    const [showAddModal, setShowAddModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    const [addForm, setAddForm] = useState({ firstName: '', lastName: '', email: '', tempPassword: '' });
    const [removeReason, setRemoveReason] = useState('');
    const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: ''});
    const [modalError, setModalError] = useState('');
    const [pwdError, setPwdError] = useState('');
    const [pwdSuccess, setPwdSuccess] = useState('');
    const authConfig = { headers: {Authorization: `Bearer ${token}`}};
    useEffect(() => {
        if (!token) {
            navigate('/login');
            return
        }
        fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, token]);

    const fetchMembers = async () => {
        try {
            const res = await axios.get(`${apiUrl}/api/v1/team/members`, authConfig);
            setMembers(res.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to load team member');
            setLoading(false);
        }
    };

    const handleAddMember = async (e) => {
        e.preventDefault();
        setModalError('');
        try {
            await axios.post(`${apiUrl}/api/v1/team/members`, addForm, authConfig);
            setAddForm({firstName: '', lastName: '', email: '', tempPassword: ''});
            fetchMembers();
        }
        catch (err) {
            const data = err.response?.data;
            setModalError(typeof data === 'string' ? data : (data?.message || 'Failed to add member. Please check your inputs.'));
        }
    };
    const handleRemoveMember = async (e) => {
        e.preventDefault();
        setModalError('');
        if (!removeReason.trim()) {
            setModalError('Reason is required.');
            return;
        }
        try {
            await axios.put(`${apiUrl}/api/v1/team/members/${selectedMember.id}/deactivate`, { reason: removeReason }, authConfig);
            setShowRemoveModal(false);
            setRemoveReason('');
            fetchMembers();
        } catch (err) {
            const data = err.response?.data;
            setModalError(typeof data === 'string' ? data : (data?.message || 'Failed to remove member.'));
        }
    };
    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdError(''); setPwdSuccess('');
        try {
            await axios.put(`${apiUrl}/api/v1/team/change-password`, pwdForm, authConfig);
            setPwdSuccess('Password changed successfully.');
            setPwdForm({ currentPassword: '', newPassword: '' });
        } catch (err) {
            const data = err.response?.data;
            setPwdError(typeof data === 'string' ? data : (data?.message || 'Failed to change password.'));
        }
    };
    if (loading) return <div className="app"><main><div className="loading-state"><div className="spinner"></div></div></main></div>;
    return (
        <div className="app">
            <header className="app-header">
                <img className="app-logo" src="/tankly_logo.svg" alt="Tankly" />
                <div style={{ display: 'flex', gap: '12px' }}>
                    <a href="/" style={{ padding: '8px 16px', border: '1.5px solid #DADFE5', borderRadius: '8px', color: '#1C3A5E', fontWeight: 600, textDecoration: 'none' }}>
                        ← Dashboard
                    </a>
                </div>
            </header>
            <main id="main-content" className="team-page">
                <div className="team-header-container">
                    <h1 className="app-title" style={{ margin: 0 }}>My Team</h1>
                    {isAdmin && (
                        <button className="btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddModal(true)}>
                            + Add Member
                        </button>
                    )}
                </div>
                {error && <div className="alert alert-error"><AlertIcon /> {error}</div>}
                <div className="team-list">
                    {members.map(m => (
                        <div key={m.id} className={`member-card ${!m.isActive ? 'deactivated' : ''}`}>
                            <div className="member-info">
                                <h3>{m.firstName} {m.lastName} {m.email === currentUserEmail && '(You)'}</h3>
                                <p>{m.email}</p>
                                <div className="member-badges">
                                    <span className={`badge ${m.role === 'ROLE_COMPANY_ADMIN' ? 'badge-admin' : 'badge-dispatcher'}`}>
                                        {m.role === 'ROLE_COMPANY_ADMIN' ? 'Admin' : 'Dispatcher'}
                                    </span>
                                    {!m.isActive && <span className="badge badge-deactivated">Deactivated</span>}
                                </div>
                            </div>
                            {isAdmin && m.isActive && m.email !== currentUserEmail && (
                                <button className="btn-secondary" style={{ width: 'auto', color: '#dc2626', borderColor: '#fca5a5' }}
                                        onClick={() => { setSelectedMember(m); setShowRemoveModal(true); }}>
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="password-section">
                    <h2>Change my password</h2>
                    {pwdError && <div className="alert alert-error"><AlertIcon /> {pwdError}</div>}
                    {pwdSuccess && <div className="alert" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>✓ {pwdSuccess}</div>}

                    <form onSubmit={handleChangePassword} style={{ maxWidth: '400px', marginTop: '1rem' }}>
                        <div className="field">
                            <label>Current Password</label>
                            <input type="password" value={pwdForm.currentPassword} onChange={e => setPwdForm({...pwdForm, currentPassword: e.target.value})} required />
                        </div>
                        <div className="field">
                            <label>New Password</label>
                            <input type="password" value={pwdForm.newPassword} onChange={e => setPwdForm({...pwdForm, newPassword: e.target.value})} required minLength={8} />
                        </div>
                        <button type="submit" className="btn-primary">Update Password</button>
                    </form>
                </div>
                {/* Add Member Modal */}
                {showAddModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>Add Team Member</h2>
                            {modalError && <div className="alert alert-error"><AlertIcon /> {modalError}</div>}
                            <form onSubmit={handleAddMember}>
                                <div className="field"><label>First Name</label><input required value={addForm.firstName} onChange={e => setAddForm({...addForm, firstName: e.target.value})} /></div>
                                <div className="field"><label>Last Name</label><input required value={addForm.lastName} onChange={e => setAddForm({...addForm, lastName: e.target.value})} /></div>
                                <div className="field"><label>Email</label><input type="email" required value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} /></div>
                                <div className="field"><label>Temporary Password</label><input type="text" required minLength={8} value={addForm.tempPassword} onChange={e => setAddForm({...addForm, tempPassword: e.target.value})} /></div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary" style={{ width: 'auto' }}>Add Member</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {/* Remove Member Modal */}
                {showRemoveModal && selectedMember && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>Remove {selectedMember.firstName}?</h2>
                            <p style={{ color: 'var(--text-secondary)' }}>They will no longer be able to log in to Tankly.</p>
                            {modalError && <div className="alert alert-error"><AlertIcon /> {modalError}</div>}
                            <form onSubmit={handleRemoveMember}>
                                <div className="field">
                                    <label>Reason for removal <span style={{color: '#ef4444'}}>*</span></label>
                                    <textarea required value={removeReason} onChange={e => setRemoveReason(e.target.value)} rows={3} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-300)' }} />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowRemoveModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-danger">Remove Member</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};
export default TeamPage;